import type { LucideIcon } from 'lucide-react';
import { CircleAlert, Inbox, LoaderCircle, Lock, RefreshCw, SearchX } from 'lucide-react';
import type { ReactNode } from 'react';
import { isApiError } from '@/lib/api/client';
import { cx } from '@/lib/cx';
import { t } from '@/lib/i18n';
import { Button } from './Button';
import { SkeletonText } from './Skeleton';
import s from './State.module.css';
import { TelemetryTrace } from './TelemetryTrace';

/** Every async view has loading, empty and error states (RULES §UI-5). */
export function LoadingState({ label, variant = 'spinner', className }: { label?: string; variant?: 'spinner' | 'skeleton'; className?: string }) {
  if (variant === 'skeleton') {
    return (
      <div className={cx(s.skeleton, className)} role="status" aria-label={label ?? t('Loading…')}>
        <SkeletonText lines={4} />
      </div>
    );
  }
  return (
    <div className={cx(s.state, s.loading, className)} role="status">
      <LoaderCircle className={s.spinner} aria-hidden />
      <span className={s.loadingLabel}>{label ?? t('Loading…')}</span>
    </div>
  );
}

export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  compact,
  className,
}: {
  icon?: LucideIcon;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
  className?: string;
}) {
  return (
    <div className={cx(s.state, compact && s.compact, className)}>
      <span className={s.iconWrap}>
        <Icon className={s.icon} aria-hidden />
      </span>
      <p className={s.title}>{title}</p>
      {description && <p className={s.description}>{description}</p>}
      {!compact && <TelemetryTrace className={s.trace} />}
      {action && <div className={s.action}>{action}</div>}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
  title,
  compact,
  className,
}: {
  error?: unknown;
  onRetry?: () => void;
  title?: ReactNode;
  compact?: boolean;
  className?: string;
}) {
  const forbidden = isApiError(error, 403);
  const missing = isApiError(error, 404);
  const Icon = forbidden ? Lock : missing ? SearchX : CircleAlert;
  const heading = title ?? (forbidden ? t('You do not have access to this') : missing ? t('Not found') : t('Something went wrong'));
  const detail = error instanceof Error ? error.message : undefined;
  return (
    <div className={cx(s.state, s.error, compact && s.compact, className)} role="alert">
      <span className={cx(s.iconWrap, s.iconDanger)}>
        <Icon className={s.icon} aria-hidden />
      </span>
      <p className={s.title}>{heading}</p>
      {detail && <p className={s.description}>{detail}</p>}
      {onRetry && !forbidden && !missing && (
        <div className={s.action}>
          <Button icon={RefreshCw} onClick={onRetry} size="sm">
            {t('Try again')}
          </Button>
        </div>
      )}
    </div>
  );
}

interface QueryLike<T> {
  data: T | undefined;
  isPending: boolean;
  error: unknown;
  refetch: () => unknown;
}

/**
 * Render a TanStack query with the three states: loading, error (with retry), empty.
 * `isEmpty` decides emptiness; `children` renders the data.
 */
export function QueryView<T>({
  query,
  children,
  isEmpty,
  empty,
  loading,
}: {
  query: QueryLike<T>;
  children: (data: T) => ReactNode;
  isEmpty?: (data: T) => boolean;
  empty?: ReactNode;
  loading?: ReactNode;
}) {
  if (query.isPending) return <>{loading ?? <LoadingState />}</>;
  if (query.error || query.data === undefined) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (isEmpty?.(query.data)) return <>{empty ?? <EmptyState title={t('Nothing here yet')} />}</>;
  return <>{children(query.data)}</>;
}
